/**
 * Rebar Tool - BIM 2025 Grade
 * 
 * Comprehensive reinforcement placexport const STANDARD_HOOKS: Record<HookType, (_d: number) => RebarHook> = {
    'None': (_d) => ({ type: 'None', angle: 0, extension: 0, bendRadius: 0 }),
    'Hook_90': (_d) => ({ type: 'Hook_90', angle: 90, extension: 12 * _d, bendRadius: 4 * _d }),
    'Hook_135': (_d) => ({ type: 'Hook_135', angle: 135, extension: 6 * _d, bendRadius: 4 * _d }),
    'Hook_180': (_d) => ({ type: 'Hook_180', angle: 180, extension: 4 * _d, bendRadius: 4 * _d }),
    'Tie_90': (_d) => ({ type: 'Tie_90', angle: 90, extension: 6 * _d, bendRadius: 4 * _d }),
    'Tie_135': (_d) => ({ type: 'Tie_135', angle: 135, extension: 6 * _d, bendRadius: 4 * _d }),
};th:
 * - 80+ standard rebar shapes (ACI, BS, Eurocode)
 * - Free-form rebar creation
 * - Rebar sets with distribution rules
 * - Hook types and dimensions
 * - Cover calculation from host
 * - Bar marks and numbering
 * - Bend schedule generation
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Rebar Standards ====================

export type RebarStandard = 'ACI' | 'BS' | 'Eurocode' | 'JIS' | 'Metric' | 'Custom';

// ==================== Bar Sizes ====================

export interface RebarSize {
    name: string;           // e.g., '#4', '10M', 'Ø12'
    diameter: number;       // mm
    area: number;           // mm²
    weight: number;         // kg/m
    bendDiameter: number;   // Minimum bend diameter (x bar diameter)
}

export const STANDARD_REBAR_SIZES: Record<string, RebarSize[]> = {
    'ACI': [
        { name: '#3', diameter: 9.525, area: 71, weight: 0.561, bendDiameter: 4 },
        { name: '#4', diameter: 12.7, area: 129, weight: 0.994, bendDiameter: 4 },
        { name: '#5', diameter: 15.875, area: 200, weight: 1.552, bendDiameter: 4 },
        { name: '#6', diameter: 19.05, area: 284, weight: 2.235, bendDiameter: 4 },
        { name: '#7', diameter: 22.225, area: 387, weight: 3.045, bendDiameter: 4 },
        { name: '#8', diameter: 25.4, area: 510, weight: 3.982, bendDiameter: 5 },
        { name: '#9', diameter: 28.65, area: 645, weight: 5.059, bendDiameter: 5 },
        { name: '#10', diameter: 32.26, area: 819, weight: 6.404, bendDiameter: 5 },
        { name: '#11', diameter: 35.81, area: 1006, weight: 7.907, bendDiameter: 5 },
    ],
    'Metric': [
        { name: 'Ø6', diameter: 6, area: 28.3, weight: 0.222, bendDiameter: 4 },
        { name: 'Ø8', diameter: 8, area: 50.3, weight: 0.395, bendDiameter: 4 },
        { name: 'Ø10', diameter: 10, area: 78.5, weight: 0.617, bendDiameter: 4 },
        { name: 'Ø12', diameter: 12, area: 113, weight: 0.888, bendDiameter: 4 },
        { name: 'Ø14', diameter: 14, area: 154, weight: 1.21, bendDiameter: 4 },
        { name: 'Ø16', diameter: 16, area: 201, weight: 1.58, bendDiameter: 4 },
        { name: 'Ø20', diameter: 20, area: 314, weight: 2.47, bendDiameter: 5 },
        { name: 'Ø25', diameter: 25, area: 491, weight: 3.85, bendDiameter: 5 },
        { name: 'Ø32', diameter: 32, area: 804, weight: 6.31, bendDiameter: 5 },
        { name: 'Ø40', diameter: 40, area: 1257, weight: 9.86, bendDiameter: 6 },
    ]
};

// ==================== Hook Types ====================

export type HookType =
    | 'None'
    | 'Hook_90'      // 90° standard hook
    | 'Hook_135'     // 135° seismic hook
    | 'Hook_180'     // 180° U-hook
    | 'Tie_90'       // Stirrup tie
    | 'Tie_135';     // Seismic stirrup tie

export interface RebarHook {
    type: HookType;
    angle: number;          // degrees
    extension: number;      // mm - Tail length
    bendRadius: number;     // mm
}

export const STANDARD_HOOKS: Record<HookType, (diameter: number) => RebarHook> = {
    'None': (_d) => ({ type: 'None', angle: 0, extension: 0, bendRadius: 0 }),
    'Hook_90': (d) => ({ type: 'Hook_90', angle: 90, extension: 12 * d, bendRadius: 4 * d }),
    'Hook_135': (d) => ({ type: 'Hook_135', angle: 135, extension: 6 * d, bendRadius: 4 * d }),
    'Hook_180': (d) => ({ type: 'Hook_180', angle: 180, extension: 4 * d, bendRadius: 4 * d }),
    'Tie_90': (d) => ({ type: 'Tie_90', angle: 90, extension: 6 * d, bendRadius: 4 * d }),
    'Tie_135': (d) => ({ type: 'Tie_135', angle: 135, extension: 6 * d, bendRadius: 4 * d }),
};

// ==================== Rebar Shapes ====================

export type RebarShapeCategory =
    | 'Straight'
    | 'Bent'
    | 'Stirrup'
    | 'Spiral'
    | 'Custom';

export interface RebarShapeSegment {
    type: 'Line' | 'Arc';
    length?: number;        // mm for lines
    angle?: number;         // degrees for bends
    radius?: number;        // mm for arcs
    direction: [number, number, number]; // Unit vector
}

export interface RebarShape {
    id: string;
    name: string;
    code: string;           // Shape code (e.g., '00', '11', '51')
    category: RebarShapeCategory;
    description: string;
    segments: RebarShapeSegment[];

    // Dimension parameters (formula-based)
    parameters: string[];   // e.g., ['A', 'B', 'C', 'D']

    // Hook ends
    startHook?: HookType;
    endHook?: HookType;

    // Thumbnail/Preview
    previewPath?: string;
}

// Standard shapes library (subset - full library would have 80+)
export const STANDARD_REBAR_SHAPES: RebarShape[] = [
    // Straight bars
    {
        id: 'shape-00',
        name: 'Straight',
        code: '00',
        category: 'Straight',
        description: 'Straight bar with no bends',
        segments: [{ type: 'Line', direction: [1, 0, 0] }],
        parameters: ['A']
    },
    {
        id: 'shape-01',
        name: 'Straight with 90° Hook',
        code: '01',
        category: 'Bent',
        description: 'Straight bar with 90° hook at one end',
        segments: [{ type: 'Line', direction: [1, 0, 0] }],
        parameters: ['A', 'B'],
        endHook: 'Hook_90'
    },
    {
        id: 'shape-02',
        name: 'Straight with 180° Hooks',
        code: '02',
        category: 'Bent',
        description: 'Straight bar with 180° hooks at both ends',
        segments: [{ type: 'Line', direction: [1, 0, 0] }],
        parameters: ['A'],
        startHook: 'Hook_180',
        endHook: 'Hook_180'
    },
    // L-shaped
    {
        id: 'shape-11',
        name: 'L-Shape',
        code: '11',
        category: 'Bent',
        description: 'L-shaped bar with one 90° bend',
        segments: [
            { type: 'Line', direction: [1, 0, 0] },
            { type: 'Line', angle: 90, direction: [0, 0, 1] }
        ],
        parameters: ['A', 'B']
    },
    // U-shaped
    {
        id: 'shape-21',
        name: 'U-Shape',
        code: '21',
        category: 'Bent',
        description: 'U-shaped bar with two 90° bends',
        segments: [
            { type: 'Line', direction: [0, 0, 1] },
            { type: 'Line', angle: 90, direction: [1, 0, 0] },
            { type: 'Line', angle: 90, direction: [0, 0, -1] }
        ],
        parameters: ['A', 'B', 'C']
    },
    // Stirrups
    {
        id: 'shape-51',
        name: 'Closed Stirrup',
        code: '51',
        category: 'Stirrup',
        description: 'Rectangular closed stirrup',
        segments: [
            { type: 'Line', direction: [1, 0, 0] },
            { type: 'Line', angle: 90, direction: [0, 0, 1] },
            { type: 'Line', angle: 90, direction: [-1, 0, 0] },
            { type: 'Line', angle: 90, direction: [0, 0, -1] }
        ],
        parameters: ['A', 'B'],
        startHook: 'Tie_135',
        endHook: 'Tie_135'
    },
    {
        id: 'shape-52',
        name: 'Open Stirrup',
        code: '52',
        category: 'Stirrup',
        description: 'U-shaped open stirrup',
        segments: [
            { type: 'Line', direction: [0, 0, 1] },
            { type: 'Line', angle: 90, direction: [1, 0, 0] },
            { type: 'Line', angle: 90, direction: [0, 0, -1] }
        ],
        parameters: ['A', 'B', 'C'],
        startHook: 'Hook_135',
        endHook: 'Hook_135'
    },
    // Z-shaped
    {
        id: 'shape-31',
        name: 'Z-Shape',
        code: '31',
        category: 'Bent',
        description: 'Z-shaped bar',
        segments: [
            { type: 'Line', direction: [1, 0, 0] },
            { type: 'Line', angle: 45, direction: [1, 0, 1] },
            { type: 'Line', angle: -45, direction: [1, 0, 0] }
        ],
        parameters: ['A', 'B', 'C', 'D']
    },
    // Spiral
    {
        id: 'shape-81',
        name: 'Spiral',
        code: '81',
        category: 'Spiral',
        description: 'Helical spiral reinforcement',
        segments: [
            { type: 'Arc', direction: [0, 0, 1] }
        ],
        parameters: ['Diameter', 'Pitch', 'Height']
    }
];

// ==================== Rebar Set / Distribution ====================

export type DistributionType =
    | 'Single'                  // Single bar
    | 'MaximumSpacing'          // Space at given max
    | 'MinimumClearSpacing'     // Min clear between bars
    | 'NumberWithSpacing'       // Fixed count with spacing
    | 'FixedNumber';            // Fixed count, calculated spacing

export interface RebarDistribution {
    type: DistributionType;
    quantity?: number;          // For fixed number
    spacing?: number;           // mm
    startOffset?: number;       // mm from edge
    endOffset?: number;         // mm from edge
    includeFirst: boolean;
    includeLast: boolean;
}

// ==================== Enhanced Rebar Geometry ====================

export interface EnhancedRebarGeometryData {
    // Host relationship
    hostId: string;
    hostFace: 'Top' | 'Bottom' | 'Left' | 'Right' | 'Front' | 'Back';

    // Shape and size
    shapeId: string;
    barSize: string;
    barDiameter: number;        // mm

    // Shape dimensions (A, B, C, D, etc.)
    dimensions: Record<string, number>;

    // Hooks
    startHook: RebarHook;
    endHook: RebarHook;

    // Position and orientation
    origin: [number, number, number];
    xVector: [number, number, number];
    yVector: [number, number, number];

    // Cover
    coverOffset: number;        // mm from host face

    // Distribution (for rebar sets)
    distribution: RebarDistribution;

    // Calculated values
    barLength: number;          // mm - Developed length
    weight: number;             // kg
    numberOfBars: number;
    totalLength: number;        // mm - All bars combined
    totalWeight: number;        // kg - All bars combined

    // 3D path for visualization
    pathPoints: [number, number, number][];
}

// ==================== Enhanced Rebar Element ====================

export interface EnhancedRebarElement extends Element {
    category: 'Structural Rebar';
    geometryData: EnhancedRebarGeometryData;

    // Scheduling
    barMark: string;            // Unique mark for scheduling
    partition: string;          // Grouping (e.g., 'Foundation', 'Beam')

    // Status
    status: 'Placed' | 'Fabricated' | 'Installed';
}

// ==================== Tool Options ====================

export interface RebarToolOptions {
    // Shape selection
    shapeId: string;

    // Bar size
    standard: RebarStandard;
    barSizeName: string;

    // Placement
    placementMode: 'Single' | 'Set' | 'FreeForm';
    hostFace: 'Top' | 'Bottom' | 'Left' | 'Right' | 'Front' | 'Back';
    coverOffset: number;

    // Hooks
    startHookType: HookType;
    endHookType: HookType;

    // Distribution (for Set mode)
    distribution: RebarDistribution;

    // Orientation
    rotateWithHost: boolean;
    flipDirection: boolean;
}

// ==================== Rebar Tool Class ====================

export class RebarTool extends BaseTool {
    id = 'rebar';
    name = 'Rebar';
    cursor = 'crosshair';

    private hoveredElementId: string | null = null;
    private hoveredFace: string | null = null;
    private placementPoints: THREE.Vector3[] = [];
    private options: RebarToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): RebarToolOptions {
        return {
            shapeId: 'shape-00',
            standard: 'Metric',
            barSizeName: 'Ø12',
            placementMode: 'Single',
            hostFace: 'Bottom',
            coverOffset: 40,
            startHookType: 'None',
            endHookType: 'None',
            distribution: {
                type: 'MaximumSpacing',
                spacing: 200,
                includeFirst: true,
                includeLast: true
            },
            rotateWithHost: true,
            flipDirection: false
        };
    }

    /**
     * Set rebar options from UI
     */
    setOptions(options: Partial<RebarToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Sync the active bar size / layout rule from shared tool properties so
     * the OptionsBar drives real placement (BIM rebar size + spacing editor).
     */
    activate(context: Parameters<BaseTool['activate']>[0]): void {
        super.activate(context);
        const props = context.bimStore.toolProperties;
        if (props.rebarBarDiameter) {
            // Match the closest standard size to the requested diameter.
            for (const std of ['Metric', 'ACI'] as const) {
                const sizes = STANDARD_REBAR_SIZES[std] ?? [];
                const match = sizes.find(s => Math.abs(s.diameter - props.rebarBarDiameter!) < 0.5);
                if (match) {
                    this.options.standard = std;
                    this.options.barSizeName = match.name;
                    break;
                }
            }
        }
        if (props.rebarLayoutRule) {
            const rule = props.rebarLayoutRule;
            this.options.placementMode = rule === 'Single' ? 'Single' : 'Set';
            this.options.distribution = {
                ...this.options.distribution,
                type: rule === 'Fixed Number' ? 'FixedNumber'
                    : rule === 'Max Spacing' ? 'MaximumSpacing'
                    : 'Single',
                spacing: props.rebarSpacing ?? this.options.distribution.spacing,
                quantity: props.rebarFixedCount ?? this.options.distribution.quantity,
            };
        }
        if (props.rebarSpacing) {
            this.options.distribution = { ...this.options.distribution, spacing: props.rebarSpacing };
        }
    }

    /**
     * Get current options
     */
    getOptions(): RebarToolOptions {
        return { ...this.options };
    }

    /**
     * Get available shapes
     */
    getAvailableShapes(): RebarShape[] {
        return STANDARD_REBAR_SHAPES;
    }

    /**
     * Get available bar sizes
     */
    getAvailableBarSizes(): RebarSize[] {
        return STANDARD_REBAR_SIZES[this.options.standard === 'ACI' ? 'ACI' : 'Metric'];
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.hoveredElementId) {
            const point = this.getIntersection(event);
            if (point) {
                if (this.options.placementMode === 'FreeForm') {
                    // Add point to path
                    this.placementPoints.push(point.clone());
                    this.updatePreview();
                } else {
                    // Create rebar immediately
                    this.createRebar(this.hoveredElementId, point);
                }
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const intersects = event.intersections;
        const hit = intersects.find(i => {
            const id = this.findId(i.object);
            return id && id !== 'ground-plane';
        });

        if (hit) {
            const id = this.findId(hit.object)!;
            const element = this.context.bimStore.project.elements.get(id);

            // Check if valid rebar host
            if (element && element.rebarHostData && element.rebarHostData.validHost) {
                this.hoveredElementId = id;
                this.hoveredFace = this.detectFace(hit);

                const barSize = this.getBarSize();
                const shape = this.getShape();

                this.context.appStore.setToolPreview({
                    type: 'rebar',
                    position: hit.point,
                    hostId: id,
                    face: this.hoveredFace,
                    shapeCode: shape?.code || '00',
                    barDiameter: barSize?.diameter || 12,
                    color: '#00ff00',
                    isValid: true
                });
            } else {
                this.hoveredElementId = null;
                this.hoveredFace = null;
                this.context.appStore.setToolPreview({
                    type: 'rebar',
                    position: hit.point,
                    hostId: id,
                    color: '#ff0000',
                    isValid: false
                });
            }
        } else {
            this.hoveredElementId = null;
            this.hoveredFace = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.placementPoints = [];
            this.context?.appStore.setToolPreview(null);
            super.onKeyDown(event);
        } else if (event.key === 'Enter' && this.options.placementMode === 'FreeForm') {
            // Finish free-form rebar
            if (this.placementPoints.length >= 2 && this.hoveredElementId) {
                this.createFreeFormRebar(this.hoveredElementId);
            }
            this.placementPoints = [];
        }
    }

    private detectFace(hit: THREE.Intersection): string {
        const normal = hit.face?.normal;
        if (!normal) return 'Top';

        // Determine face based on normal direction
        const absX = Math.abs(normal.x);
        const absY = Math.abs(normal.y);
        const absZ = Math.abs(normal.z);

        if (absZ >= absX && absZ >= absY) {
            return normal.z > 0 ? 'Top' : 'Bottom';
        } else if (absX >= absY) {
            return normal.x > 0 ? 'Right' : 'Left';
        } else {
            return normal.y > 0 ? 'Front' : 'Back';
        }
    }

    private getBarSize(): RebarSize | undefined {
        const sizes = this.getAvailableBarSizes();
        return sizes.find(s => s.name === this.options.barSizeName);
    }

    private getShape(): RebarShape | undefined {
        return STANDARD_REBAR_SHAPES.find(s => s.id === this.options.shapeId);
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.placementPoints.length > 0) {
            this.context.appStore.setToolPreview({
                type: 'rebarPath',
                points: this.placementPoints.map(p => p.toArray()),
                barDiameter: this.getBarSize()?.diameter || 12
            });
        }
    }

    private createRebar(hostId: string, point: THREE.Vector3): void {
        if (!this.context) return;

        const host = this.context.bimStore.project.elements.get(hostId);
        if (!host || !host.rebarHostData) return;

        const barSize = this.getBarSize();
        const shape = this.getShape();
        if (!barSize || !shape) return;

        // Calculate placement
        const hostGeom = host.geometryData as any;

        // Generate rebar path based on shape
        const pathPoints = this.generateRebarPath(point, shape, barSize, hostGeom);

        // Calculate hooks
        const startHook = STANDARD_HOOKS[this.options.startHookType](barSize.diameter);
        const endHook = STANDARD_HOOKS[this.options.endHookType](barSize.diameter);

        // Calculate bar length
        const barLength = this.calculateBarLength(pathPoints, startHook, endHook, barSize);
        const weight = (barLength / 1000) * barSize.weight;

        // Calculate distribution
        const { numberOfBars, totalLength, totalWeight } = this.calculateDistribution(
            barLength,
            barSize.weight,
            hostGeom
        );

        const geometryData: EnhancedRebarGeometryData = {
            hostId,
            hostFace: this.options.hostFace as any,
            shapeId: shape.id,
            barSize: barSize.name,
            barDiameter: barSize.diameter,
            dimensions: this.getDefaultDimensions(shape, hostGeom),
            startHook,
            endHook,
            origin: [point.x, point.y, point.z],
            xVector: [1, 0, 0],
            yVector: [0, 1, 0],
            coverOffset: this.options.coverOffset,
            distribution: { ...this.options.distribution },
            barLength,
            weight,
            numberOfBars,
            totalLength,
            totalWeight,
            pathPoints: pathPoints.map(p => [p.x, p.y, p.z] as [number, number, number])
        };

        const rebar: EnhancedRebarElement = {
            id: uuidv4(),
            category: 'Structural Rebar',
            type: `Rebar Bar: ${barSize.name}`,
            parameters: this.buildParameters(geometryData, shape),
            constraints: this.buildRebarConstraints(hostId),
            geometryData,
            barMark: this.generateBarMark(),
            partition: this.determinePartition(host),
            status: 'Placed'
        };

        this.context.bimStore.addElement(rebar);
    }

    private createFreeFormRebar(hostId: string): void {
        if (!this.context || this.placementPoints.length < 2) return;

        const host = this.context.bimStore.project.elements.get(hostId);
        if (!host || !host.rebarHostData) return;

        const barSize = this.getBarSize();
        if (!barSize) return;

        const pathPoints = this.placementPoints.map(p => p.clone());

        // Calculate length from path
        let barLength = 0;
        for (let i = 1; i < pathPoints.length; i++) {
            barLength += pathPoints[i].distanceTo(pathPoints[i - 1]) * 1000; // Convert to mm
        }

        const weight = (barLength / 1000) * barSize.weight;

        const geometryData: EnhancedRebarGeometryData = {
            hostId,
            hostFace: this.options.hostFace as any,
            shapeId: 'custom',
            barSize: barSize.name,
            barDiameter: barSize.diameter,
            dimensions: { 'Length': barLength },
            startHook: STANDARD_HOOKS[this.options.startHookType](barSize.diameter),
            endHook: STANDARD_HOOKS[this.options.endHookType](barSize.diameter),
            origin: pathPoints[0].toArray() as [number, number, number],
            xVector: [1, 0, 0],
            yVector: [0, 1, 0],
            coverOffset: this.options.coverOffset,
            distribution: { type: 'Single', includeFirst: true, includeLast: true },
            barLength,
            weight,
            numberOfBars: 1,
            totalLength: barLength,
            totalWeight: weight,
            pathPoints: pathPoints.map(p => p.toArray() as [number, number, number])
        };

        const rebar: EnhancedRebarElement = {
            id: uuidv4(),
            category: 'Structural Rebar',
            type: `Rebar Bar: ${barSize.name}`,
            parameters: {
                'Bar Diameter': barSize.diameter,
                'Bar Size': barSize.name,
                'Shape': 'Free Form',
                'Total Bar Length': barLength,
                'Weight': weight,
                'Host': hostId
            },
            constraints: this.buildRebarConstraints(hostId),
            geometryData,
            barMark: this.generateBarMark(),
            partition: this.determinePartition(host),
            status: 'Placed'
        };

        this.context.bimStore.addElement(rebar);
        this.placementPoints = [];
    }

    private generateRebarPath(
        origin: THREE.Vector3,
        shape: RebarShape,
        barSize: RebarSize,
        hostGeom: any
    ): THREE.Vector3[] {
        const points: THREE.Vector3[] = [origin.clone()];
        let current = origin.clone();

        // Get dimensions from host or defaults
        const dims = this.getDefaultDimensions(shape, hostGeom);

        // Generate points based on shape segments
        for (let i = 0; i < shape.segments.length; i++) {
            const seg = shape.segments[i];
            const paramName = shape.parameters[i] || 'A';
            const length = dims[paramName] || 500; // Default 500mm

            if (seg.type === 'Line') {
                const dir = new THREE.Vector3(...seg.direction).normalize();
                current = current.clone().add(dir.multiplyScalar(length / 1000));
                points.push(current);
            }
        }

        return points;
    }

    private getDefaultDimensions(shape: RebarShape, hostGeom: any): Record<string, number> {
        const dims: Record<string, number> = {};

        // Try to get dimensions from host geometry
        if (hostGeom) {
            if (shape.category === 'Stirrup') {
                // For stirrups, use host cross-section
                dims['A'] = (hostGeom.profile?.width || hostGeom.width || 300) - 2 * this.options.coverOffset;
                dims['B'] = (hostGeom.profile?.depth || hostGeom.depth || 600) - 2 * this.options.coverOffset;
            } else {
                // For longitudinal bars, use host length
                dims['A'] = hostGeom.actualLength ? hostGeom.actualLength * 1000 : 3000;
            }
        }

        // Fill in missing dimensions with defaults
        shape.parameters.forEach((param, i) => {
            if (!dims[param]) {
                dims[param] = 500 * (i + 1); // Default: A=500, B=1000, etc.
            }
        });

        return dims;
    }

    private calculateBarLength(
        pathPoints: THREE.Vector3[],
        startHook: RebarHook,
        endHook: RebarHook,
        barSize: RebarSize
    ): number {
        let length = 0;

        // Sum path segments
        for (let i = 1; i < pathPoints.length; i++) {
            length += pathPoints[i].distanceTo(pathPoints[i - 1]) * 1000; // mm
        }

        // Add hook extensions
        length += startHook.extension;
        length += endHook.extension;

        // Add bend allowances
        const bendCount = pathPoints.length - 2;
        const bendAllowance = Math.PI * barSize.bendDiameter * barSize.diameter / 4;
        length += bendCount * bendAllowance;

        return Math.round(length);
    }

    private calculateDistribution(
        barLength: number,
        barWeight: number,
        hostGeom: any
    ): { numberOfBars: number; totalLength: number; totalWeight: number } {
        const dist = this.options.distribution;
        let numberOfBars = 1;

        if (dist.type === 'Single') {
            numberOfBars = 1;
        } else if (dist.type === 'FixedNumber' && dist.quantity) {
            numberOfBars = dist.quantity;
        } else if (dist.type === 'MaximumSpacing' && dist.spacing && hostGeom) {
            // Calculate from host length
            const hostLength = hostGeom.actualLength ? hostGeom.actualLength * 1000 : 3000;
            numberOfBars = Math.ceil(hostLength / dist.spacing);
        } else if (dist.type === 'NumberWithSpacing' && dist.quantity) {
            numberOfBars = dist.quantity;
        }

        const totalLength = barLength * numberOfBars;
        const totalWeight = (barLength / 1000) * barWeight * numberOfBars;

        return { numberOfBars, totalLength, totalWeight };
    }

    private buildParameters(geom: EnhancedRebarGeometryData, shape: RebarShape): Record<string, any> {
        return {
            // Identity
            'Shape': shape?.code || 'Custom',
            'Bar Diameter': geom.barDiameter,
            'Bar Size': geom.barSize,

            // Dimensions
            ...Object.fromEntries(
                Object.entries(geom.dimensions).map(([k, v]) => [`Dimension ${k}`, v])
            ),

            // Hooks
            'Start Hook': geom.startHook.type,
            'End Hook': geom.endHook.type,

            // Placement
            'Host': geom.hostId,
            'Host Face': geom.hostFace,
            'Cover': geom.coverOffset,

            // Calculated
            'Total Bar Length': geom.totalLength,
            'Quantity': geom.numberOfBars,
            'Total Weight': geom.totalWeight
        };
    }

    private buildRebarConstraints(hostId: string): Constraint[] {
        return [
            {
                type: 'HOST_CONTAINED',
                targetId: hostId,
                data: {
                    face: this.options.hostFace,
                    coverOffset: this.options.coverOffset
                }
            }
        ];
    }

    private generateBarMark(): string {
        // Generate unique bar mark (in production, would be from project sequence)
        return `M${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
    }

    private determinePartition(host: Element): string {
        // Determine partition from host category
        switch (host.category) {
            case 'Floors': return 'Slab';
            case 'Walls': return 'Wall';
            case 'Structural Framing': return 'Beam';
            case 'Structural Columns': return 'Column';
            default: return 'Other';
        }
    }
}

// ==================== Helper Functions ====================

/**
 * Calculate bend schedule for a rebar element
 */
export function generateBendSchedule(rebar: EnhancedRebarElement): {
    mark: string;
    shape: string;
    diameter: number;
    dimensions: Record<string, number>;
    quantity: number;
    length: number;
    weight: number;
} {
    const geom = rebar.geometryData;

    return {
        mark: rebar.barMark,
        shape: geom.shapeId,
        diameter: geom.barDiameter,
        dimensions: geom.dimensions,
        quantity: geom.numberOfBars,
        length: geom.barLength,
        weight: geom.totalWeight
    };
}

/**
 * Get all rebar shapes
 */
export function getAllRebarShapes(): RebarShape[] {
    return STANDARD_REBAR_SHAPES;
}

/**
 * Get rebar shapes by category
 */
export function getRebarShapesByCategory(category: RebarShapeCategory): RebarShape[] {
    return STANDARD_REBAR_SHAPES.filter(s => s.category === category);
}
